import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

export const CRITICAL_STORAGE_KEYS = {
  previewAnonUid: 'previewAnonUid',
  onboardingProgress: 'onboardingProgress',
  pendingFavoriteChanges: 'pendingFavoriteChanges',
  previewLikedRings: 'previewLikedRings',
  previewStyleProfile: 'previewStyleProfile',
  pinterestAuthStarted: 'pinterest_auth_started',
};

const nativeCriticalKeys = Object.values(CRITICAL_STORAGE_KEYS);
const NATIVE_FIREBASE_AUTH_CACHE_KEY = 'firebase_auth_cache_v1';
const FIREBASE_STORAGE_PREFIX = 'firebase:';

const hasWindow = () => typeof window !== 'undefined';
const isNativeApp = () => Capacitor.isNativePlatform();

const safeLocalStorage = {
  get(key) {
    if (!hasWindow()) return null;
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      console.warn(`[storage] Failed to read localStorage key "${key}"`, err);
      return null;
    }
  },
  set(key, value) {
    if (!hasWindow()) return;
    try {
      window.localStorage.setItem(key, value);
    } catch (err) {
      console.warn(`[storage] Failed to set localStorage key "${key}"`, err);
    }
  },
  remove(key) {
    if (!hasWindow()) return;
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      console.warn(`[storage] Failed to remove localStorage key "${key}"`, err);
    }
  },
};

const syncNativeValue = async (key, value) => {
  if (!isNativeApp()) return;
  try {
    if (value === null || value === undefined) {
      await Preferences.remove({ key });
    } else {
      await Preferences.set({ key, value });
    }
  } catch (err) {
    console.warn(`[storage] Failed to sync native value for "${key}"`, err);
  }
};

export const getStoredItem = (key) => safeLocalStorage.get(key);

export const setStoredItem = (key, value) => {
  if (value === null || value === undefined) {
    removeStoredItem(key);
    return;
  }

  safeLocalStorage.set(key, value);
  void syncNativeValue(key, value);
};

export const removeStoredItem = (key) => {
  safeLocalStorage.remove(key);
  void syncNativeValue(key, null);
};

export const getStoredJsonItem = (key, fallback = null) => {
  const rawValue = getStoredItem(key);
  if (!rawValue) return fallback;

  try {
    return JSON.parse(rawValue);
  } catch (err) {
    console.warn(`[storage] Failed to parse JSON for "${key}"`, err);
    return fallback;
  }
};

export const setStoredJsonItem = (key, value) => {
  setStoredItem(key, JSON.stringify(value));
};

export const hydrateCriticalStorage = async (keys = nativeCriticalKeys) => {
  if (!isNativeApp() || !hasWindow()) return;

  await Promise.all(
    keys.map(async (key) => {
      const localValue = safeLocalStorage.get(key);

      try {
        const { value: nativeValue } = await Preferences.get({ key });

        if (nativeValue !== null && nativeValue !== undefined) {
          if (nativeValue !== localValue) {
            safeLocalStorage.set(key, nativeValue);
          }
          return;
        }

        if (localValue !== null && localValue !== undefined) {
          await Preferences.set({ key, value: localValue });
        }
      } catch (err) {
        console.warn(`[storage] Failed to hydrate key "${key}"`, err);
      }
    })
  );
};

const getFirebaseAuthStorageSnapshot = () => {
  if (!hasWindow()) return {};

  try {
    return Object.keys(window.localStorage)
      .filter((key) => key.startsWith(FIREBASE_STORAGE_PREFIX))
      .reduce((snapshot, key) => {
        const value = window.localStorage.getItem(key);
        if (value !== null) {
          snapshot[key] = value;
        }
        return snapshot;
      }, {});
  } catch (err) {
    console.warn('[storage] Failed to capture Firebase auth storage snapshot', err);
    return {};
  }
};

export const persistFirebaseAuthStorage = async () => {
  if (!isNativeApp()) return;

  try {
    const snapshot = getFirebaseAuthStorageSnapshot();
    await Preferences.set({
      key: NATIVE_FIREBASE_AUTH_CACHE_KEY,
      value: JSON.stringify(snapshot),
    });
  } catch (err) {
    console.warn('[storage] Failed to persist Firebase auth storage', err);
  }
};

export const hydrateFirebaseAuthStorage = async () => {
  if (!isNativeApp() || !hasWindow()) return;

  try {
    const { value } = await Preferences.get({ key: NATIVE_FIREBASE_AUTH_CACHE_KEY });
    if (!value) return;

    const snapshot = JSON.parse(value);
    if (!snapshot || typeof snapshot !== 'object') {
      return;
    }

    Object.entries(snapshot).forEach(([key, storedValue]) => {
      if (!key.startsWith(FIREBASE_STORAGE_PREFIX) || typeof storedValue !== 'string') {
        return;
      }

      safeLocalStorage.set(key, storedValue);
    });
  } catch (err) {
    console.warn('[storage] Failed to hydrate Firebase auth storage', err);
  }
};
